/**
 * useRouteTitle - derives a FALLBACK topbar title from the current route.
 *
 * Used by TopBar only when a page hasn't explicitly set a title via
 * useSetTopBarTitle(). This gives every route — including third-party pages we
 * don't own (Settings, Catalog, Docs, etc.) — a sensible title with zero
 * per-page wiring. Pages that DO call useSetTopBarTitle always override this.
 *
 * To add/adjust a fallback title, update the route key map and shell locale
 * entries (longest-prefix match).
 */
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type RouteTitle = { title: string; subtitle?: string };

const ROUTE_TITLE_KEYS: Record<string, { title: string; subtitle?: string }> = {
  '/catalog': { title: 'routeTitles.catalog' },
  '/docs': { title: 'routeTitles.docs' },
  '/create': { title: 'routeTitles.create' },
  '/api-docs': { title: 'routeTitles.apiDocs' },
  '/catalog-import': { title: 'routeTitles.catalogImport' },
  '/search': { title: 'routeTitles.search' },
  '/catalog-graph': { title: 'routeTitles.catalogGraph' },
  '/notifications': { title: 'routeTitles.notifications' },
  // Requirement pages set their own titles via useSetTopBarTitle.
};

export function useRouteTitle(): RouteTitle | undefined {
  const { pathname } = useLocation();
  const { t } = useTranslation('shell');

  const match = Object.keys(ROUTE_TITLE_KEYS)
    .filter(k => pathname === k || pathname.startsWith(`${k}/`))
    .sort((a, b) => b.length - a.length)[0];
  // eslint-disable-next-line security/detect-object-injection
  // eslint-disable-next-line security/detect-object-injection
  const routeTitle = match ? ROUTE_TITLE_KEYS[match] : undefined;

  return routeTitle
    ? {
        title: t(routeTitle.title),
        ...(routeTitle.subtitle ? { subtitle: t(routeTitle.subtitle) } : {}),
      }
    : undefined;
}
